import { DestroyRef, inject } from '@angular/core';

export const COMPUTING_POLL_INTERVAL_MS = 5_000;

interface ComputingPollOptions {
  /** Silent refetch. Runs every interval once the loop is armed; its result feeds
   *  notify() — a settled page stops the loop, anything else (still computing, error,
   *  stale) leaves it running so the badge always converges. */
  readonly refresh: () => void;
  /** A tick that lands while blocked (inline edit, staged draft) re-arms instead of
   *  refetching, so the loop survives the block without churning the grid. */
  readonly isBlocked?: () => boolean;
}

interface ComputingPollHandle {
  /** Feed every fetched page's `computing` flag: true arms/keeps the loop running,
   *  false stops it once the calc queue has settled. */
  readonly notify: (computing: boolean) => void;
}

/** Convergence loop behind the 'Calcul en cours' badge: while the server reports the
 *  measure's recalc queue active (`page.computing`), silently refetch every
 *  COMPUTING_POLL_INTERVAL_MS so the per-row statuses always land on the truth — even
 *  when a terminal websocket frame is lost. Must be called from an injection context;
 *  the pending timer dies with the host. */
export function registerComputingPoll(options: ComputingPollOptions): ComputingPollHandle {
  const destroyRef = inject(DestroyRef);
  let timerId: ReturnType<typeof setTimeout> | null = null;

  const clear = (): void => {
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
  };
  const arm = (): void => {
    clear();
    timerId = setTimeout(() => {
      timerId = null;
      if (options.isBlocked?.()) {
        arm();
        return;
      }
      options.refresh();
      // Self-perpetuate: a refetch that errors or lands stale never feeds notify(),
      // so re-arm here too. Only a settled page (notify(false)) or host teardown ends
      // the loop — otherwise a single failed tick would kill the badge's convergence
      // exactly when it must cover a lost terminal websocket frame.
      arm();
    }, COMPUTING_POLL_INTERVAL_MS);
  };

  destroyRef.onDestroy(clear);

  return {
    notify: computing => {
      if (computing) {
        arm();
      } else {
        clear();
      }
    },
  };
}
